import React, { useCallback, useEffect, useState } from "react";
import { StyleSheet, TouchableOpacity, View } from "react-native";
import { useForm, Controller } from "react-hook-form";
import { DatePickerInput } from "react-native-paper-dates";

import {
  useCreateAssessmentMutation,
  useDeleteAssessmentMutation,
  useGenerateAssessmentQuestionBankMutation,
  useGetAssessmentAiTutorsQuery,
  useGetAssessmentQuestionBankGenerationStatusLazyQuery,
  useGetAssessmentQuestionBankLazyQuery,
  useUpdateAssessmentConfigMutation,
  Module,
  AiTutor,
  ActivityState,
  AssessmentType,
  McqQuestion,
} from "../../../../graphql/generated/graphql";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomTextInput from "../../../common/general/CustomTextInput/CustomTextInput";
import CustomSpacing from "../../../common/layout/CustomSpacing/CustomSpacing";
import FormLayout from "../common/FormLayout";
import AssessmentFormAccordion from "./AssessmentFormAccordion";
import CustomButton from "../../../common/general/CustomButton/CustomButton";
import AssessmentQuestionBankModal from "./AssessmentQuestionBankModal";
import RadioButton from "../../../common/form/RadioButton/RadioButton";
import { useToast } from "../../../../contexts/Toast/ToastProvider";

interface ActivityData {
  id?: string;
  assessmentId?: string;
  assessmentType?: AssessmentType;
  numberOfQuestions?: number;
  timeLimit?: number;
  aiTutorIds?: string[];
  type?: string;
  selectedAiTutorIds?: string[];
  dueDate?: string | Date | null;
  activityState: ActivityState | undefined;
  scheduledDate: string | Date | undefined;
}

interface AssessmentFormProps {
  activityData: ActivityData;
  onSave: (data: ActivityData) => void | Promise<void>;
  onCancel: () => void;
  error: string;
  isSubmitting: boolean;
  isNewActivity: boolean;
  activeModuleData: Module;
  courseId: string;
}

const DEFAULT_NUMBER_OF_QUESTIONS = 10;
const DEFAULT_TIME_LIMIT = 0;
const MAX_NUMBER_OF_QUESTIONS = 25;
const MAX_TIME_LIMIT = 180;
const ASSESSMENT_JOB_PENDING_STATUSES = new Set([
  "queued",
  "waiting",
  "active",
  "delayed",
  "paused",
  "waiting-children",
]);
const ASSESSMENT_GENERATION_MAX_POLL_ERRORS = 3;
const ASSESSMENT_GENERATION_POLL_INTERVAL_MS = 2000;
const ASSESSMENT_GENERATION_TIMEOUT_MS = 3 * 60 * 1000; // 3 minutes
const QUESTION_BANK_PAGE_SIZE = 25;
const DEFAULT_ASSESSMENT_TYPE: AssessmentType = AssessmentType.SocraticDialog;
const NO_AI_TUTORS_ERROR = "Please select at least one interactive learning experience";
const ASSESSMENT_TYPE_LOCKED_MESSAGE = "Assessment type is locked after creation.";
const NUMBER_OF_QUESTIONS_REQUIRED_ERROR = "Number of questions is required.";
const NUMBER_OF_QUESTIONS_RANGE_ERROR = `Number of questions must be between 1 and ${MAX_NUMBER_OF_QUESTIONS}.`;
const TIME_LIMIT_RANGE_ERROR = `Time limit must be between 1 and ${MAX_TIME_LIMIT} minutes.`;
const buildInsufficientPoolMessage = (numberOfQuestions: number, poolCount: number) =>
  `Number of questions (${numberOfQuestions}) cannot be greater than the question pool size (${poolCount}).`;

const maybeFormatDueDate = (date: unknown): Date | null => {
  if (!date) return null;
  if (date instanceof Date) return date;
  if (typeof date === "string") {
    const parsed = new Date(date);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const isBlankInput = (value: unknown): boolean => {
  return value === undefined || value === null || (typeof value === "string" && value.trim() === "");
};

const parseIntegerInput = (value: unknown): number | null => {
  if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      return null;
    }

    return Math.trunc(value);
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return null;
    }

    if (!/^-?\d+$/.test(trimmedValue)) {
      return null;
    }

    const parsedValue = Number.parseInt(trimmedValue, 10);
    if (Number.isNaN(parsedValue)) {
      return null;
    }

    return parsedValue;
  }

  return null;
};

const toInputStringValue = (value: unknown): string => {
  if (value === undefined || value === null) {
    return "";
  }

  return String(value);
};

const AssessmentForm = ({
  activityData,
  onSave,
  onCancel,
  error,
  isSubmitting,
  isNewActivity = false,
  activeModuleData,
  courseId,
}: AssessmentFormProps) => {
  const { data: assessmentAiTutors } = useGetAssessmentAiTutorsQuery({
    variables: {
      moduleId: activeModuleData.id,
      courseId,
    },
    fetchPolicy: "no-cache",
    skip: !activeModuleData || !courseId,
  });

  const { showToast } = useToast();

  const [validationError, setValidationError] = useState("");
  const [showPastModules, setShowPastModules] = useState(false);
  const [showFutureModules, setShowFutureModules] = useState(false);
  const [openPastAccordionIndices, setOpenPastAccordionIndices] = useState<number[]>([]);
  const [openFutureAccordionIndices, setOpenFutureAccordionIndices] = useState<number[]>([]);
  const [isQuestionBankOpen, setIsQuestionBankOpen] = useState(false);
  const [isGeneratingQuestionBank, setIsGeneratingQuestionBank] = useState(false);
  const [generatedAssessmentId, setGeneratedAssessmentId] = useState<string | null>(activityData?.id ?? null);
  const [pendingCreatePayload, setPendingCreatePayload] = useState<ActivityData | null>(null);
  const [socraticQuestionBankPage, setSocraticQuestionBankPage] = useState(1);
  const [mcqQuestionBankPage, setMcqQuestionBankPage] = useState(1);
  const [socraticQuestionBankTotal, setSocraticQuestionBankTotal] = useState(0);
  const [mcqQuestionBankTotal, setMcqQuestionBankTotal] = useState(0);
  const [questionGenerationFailed, setQuestionGenerationFailed] = useState(false);

  const assessmentId = isNewActivity ? generatedAssessmentId : activityData?.id;
  const hasQuestions = socraticQuestionBankTotal + mcqQuestionBankTotal > 0;
  const canViewQuestionBank =
    Boolean(assessmentId) && !isGeneratingQuestionBank && (hasQuestions || !questionGenerationFailed);
  const questionBankButtonText = isGeneratingQuestionBank ? "Loading Question Bank..." : "View Question Bank";
  const questionBankStatusMessage = isGeneratingQuestionBank
    ? "Loading question bank. Please wait for question generation to finish."
    : isNewActivity
      ? "Generate questions first to view this assessment question bank."
      : "Question bank not available.";

  const [fetchQuestionBank, { data: questionBankData, loading: questionBankLoading, error: questionBankError }] =
    useGetAssessmentQuestionBankLazyQuery({
      fetchPolicy: "no-cache",
    });
  const [createAssessmentMutation] = useCreateAssessmentMutation();
  const [deleteAssessmentMutation] = useDeleteAssessmentMutation();
  const [updateAssessmentConfigMutation] = useUpdateAssessmentConfigMutation();
  const [generateAssessmentQuestionBankMutation] = useGenerateAssessmentQuestionBankMutation();
  const [getAssessmentQuestionBankGenerationStatus] = useGetAssessmentQuestionBankGenerationStatusLazyQuery();

  const questionBankDataNode = questionBankData?.getAssessmentQuestionBank?.data;
  const questionBankAiTutors = questionBankDataNode?.aiTutors || [];
  const questionBankMcqQuestions = questionBankDataNode?.mcqQuestions || [];

  const { control, handleSubmit, setValue, watch } = useForm({
    defaultValues: {
      assessmentType: activityData?.assessmentType ?? DEFAULT_ASSESSMENT_TYPE,
      numberOfQuestions: activityData?.numberOfQuestions ?? DEFAULT_NUMBER_OF_QUESTIONS,
      timeLimit: activityData?.timeLimit,
      selectedAiTutors: activityData?.selectedAiTutorIds || [],
      dueDate: maybeFormatDueDate(activityData?.dueDate),
      activityState: activityData?.activityState || "draft",
      scheduledDate: activityData?.scheduledDate || undefined,
    },
  });

  useEffect(() => {
    if (!isNewActivity) {
      setGeneratedAssessmentId(activityData?.id ?? null);
      return;
    }

    if (!activityData?.id) {
      return;
    }

    setGeneratedAssessmentId(activityData.id);
  }, [activityData?.id, isNewActivity]);

  const hasSelectedAiTutors = (moduleAiTutors: { id: string; title: string }[], selectedTutorIds: string[]) => {
    return moduleAiTutors?.some((tutor) => selectedTutorIds?.includes(tutor.id));
  };

  useEffect(() => {
    if (
      (!activityData?.selectedAiTutorIds || activityData.selectedAiTutorIds.length === 0) &&
      assessmentAiTutors?.getAssessmentAiTutors?.data?.currentModuleAiTutors?.aiTutors
    ) {
      const aiTutors = assessmentAiTutors.getAssessmentAiTutors.data.currentModuleAiTutors.aiTutors;
      setValue(
        "selectedAiTutors",
        aiTutors.map((tutor) => tutor.id)
      );
    }
  }, [assessmentAiTutors, setValue, activityData?.selectedAiTutorIds]);

  useEffect(() => {
    if (isNewActivity) {
      return;
    }

    setValue("assessmentType", activityData?.assessmentType ?? DEFAULT_ASSESSMENT_TYPE);
  }, [isNewActivity, setValue, activityData?.assessmentType]);

  const selectedAiTutors = watch("selectedAiTutors");
  const watchActivityState = watch("activityState");
  const watchAssessmentType = watch("assessmentType") ?? activityData?.assessmentType ?? DEFAULT_ASSESSMENT_TYPE;
  const parsedConfiguredQuestionCount = parseIntegerInput(watch("numberOfQuestions"));
  const normalizedConfiguredQuestionCount =
    parsedConfiguredQuestionCount === null ? 0 : Math.max(0, Math.trunc(parsedConfiguredQuestionCount));
  const isAssessmentTypeLocked = !isNewActivity;
  const setValidationErrorWithToast = useCallback(
    (message: string) => {
      setValidationError(message);
      showToast({
        message,
        type: "error",
      });
    },
    [showToast]
  );

  const rollbackAndClose = useCallback(
    async (message: string, assessmentIdToDelete: string | null, didCreateInThisFlow: boolean) => {
      if (didCreateInThisFlow && assessmentIdToDelete) {
        try {
          await deleteAssessmentMutation({ variables: { id: assessmentIdToDelete } });
        } catch {
          // Best-effort: still reset state and close
        }
      }
      setQuestionGenerationFailed(true);
      setGeneratedAssessmentId(null);
      setPendingCreatePayload(null);
      setValidationErrorWithToast(message);
      onCancel();
    },
    [deleteAssessmentMutation, onCancel, setValidationErrorWithToast]
  );

  useEffect(() => {
    if (selectedAiTutors && selectedAiTutors.length > 0) {
      setValidationError("");
    }
  }, [selectedAiTutors]);

  const getCurrentQuestionPoolCount = async (assessmentType: AssessmentType): Promise<number | null> => {
    if (!assessmentId) {
      return null;
    }

    const { data: response } = await fetchQuestionBank({
      variables: {
        assessmentId,
        pagination: {
          socraticOffset: 0,
          socraticLimit: 1,
          mcqOffset: 0,
          mcqLimit: 1,
        },
      },
    });

    const payload = response?.getAssessmentQuestionBank;
    if (!payload) {
      throw new Error("Failed to validate question pool.");
    }

    const fallbackSocraticCount =
      payload.data?.aiTutors?.reduce((sum, tutor) => sum + (tutor.questions?.length ?? 0), 0) ?? 0;
    const fallbackMcqCount = payload.data?.mcqQuestions?.length ?? 0;

    return assessmentType === AssessmentType.Mcq
      ? (payload.mcqTotalCount ?? fallbackMcqCount)
      : (payload.socraticTotalCount ?? fallbackSocraticCount);
  };

  const waitForQuestionGenerationCompletion = useCallback(
    async (jobId: string) => {
      let pollErrorCount = 0;
      const startTime = Date.now();

      while (Date.now() - startTime < ASSESSMENT_GENERATION_TIMEOUT_MS) {
        try {
          const { data } = await getAssessmentQuestionBankGenerationStatus({
            variables: { jobId },
            fetchPolicy: "network-only",
          });
          const payload = data?.getAssessmentQuestionBankGenerationStatus;
          if (!payload) {
            pollErrorCount += 1;
            if (pollErrorCount >= ASSESSMENT_GENERATION_MAX_POLL_ERRORS) {
              throw new Error("Failed to check question generation status. Please try again.");
            }

            await new Promise((resolve) => setTimeout(resolve, ASSESSMENT_GENERATION_POLL_INTERVAL_MS));
            continue;
          }

          const normalizedStatus = (payload.status ?? "").toLowerCase();
          if (ASSESSMENT_JOB_PENDING_STATUSES.has(normalizedStatus)) {
            pollErrorCount = 0;
            await new Promise((resolve) => setTimeout(resolve, ASSESSMENT_GENERATION_POLL_INTERVAL_MS));
            continue;
          }

          if (normalizedStatus === "completed" && payload.success) {
            return;
          }

          throw new Error(payload.message || "Failed to generate questions.");
        } catch (statusError) {
          pollErrorCount += 1;
          if (pollErrorCount >= ASSESSMENT_GENERATION_MAX_POLL_ERRORS) {
            throw statusError instanceof Error
              ? statusError
              : new Error("Failed to check question generation status. Please try again.");
          }

          await new Promise((resolve) => setTimeout(resolve, ASSESSMENT_GENERATION_POLL_INTERVAL_MS));
        }
      }

      throw new Error("Question generation timed out. Please try again.");
    },
    [getAssessmentQuestionBankGenerationStatus]
  );

  const handleGenerateQuestions = useCallback(
    async (payload: ActivityData & { aiTutorIds: string[]; numberOfQuestions: number; timeLimit: number }) => {
      setIsGeneratingQuestionBank(true);
      let didCreateInThisFlow = false;
      let resolvedAssessmentId: string | null = null;
      try {
        const configInput = {
          assessmentId: generatedAssessmentId ?? undefined,
          assessmentType: payload.assessmentType,
          numberOfQuestions: payload.numberOfQuestions,
          timeLimit: payload.timeLimit,
          aiTutorIds: payload.aiTutorIds,
          activityState: payload.activityState,
          scheduledDate: payload.scheduledDate,
          dueDate: payload.dueDate ?? null,
          moduleId: activeModuleData.id,
        };

        resolvedAssessmentId = generatedAssessmentId;
        if (resolvedAssessmentId) {
          const updateResponse = await updateAssessmentConfigMutation({
            variables: {
              input: {
                ...configInput,
                assessmentId: resolvedAssessmentId,
              },
            },
          });
          const updatePayload = updateResponse?.data?.updateAssessmentConfig;
          if (!updatePayload?.success) {
            throw new Error(updatePayload?.message || "Failed to update assessment before generating questions.");
          }
        } else {
          const createResponse = await createAssessmentMutation({
            variables: {
              input: configInput,
            },
          });
          const createPayload = createResponse?.data?.createAssessment;
          const createdAssessmentId = createPayload?.data?.id;
          if (!createPayload?.success || !createdAssessmentId) {
            throw new Error(createPayload?.message || "Failed to create assessment.");
          }

          resolvedAssessmentId = createdAssessmentId;
          setGeneratedAssessmentId(createdAssessmentId);
          didCreateInThisFlow = true;
        }

        if (!resolvedAssessmentId) {
          throw new Error("Failed to create assessment.");
        }

        const generateResponse = await generateAssessmentQuestionBankMutation({
          variables: {
            input: {
              assessmentId: resolvedAssessmentId,
            },
          },
        });
        const generatePayload = generateResponse?.data?.generateAssessmentQuestionBank;
        if (!generatePayload?.success || !generatePayload?.jobId) {
          await rollbackAndClose(
            generatePayload?.message || "Failed to start question generation.",
            resolvedAssessmentId,
            didCreateInThisFlow
          );
          return;
        }

        setPendingCreatePayload({
          ...payload,
          assessmentId: resolvedAssessmentId,
          id: resolvedAssessmentId,
        });

        await waitForQuestionGenerationCompletion(generatePayload.jobId);
        setSocraticQuestionBankPage(1);
        setMcqQuestionBankPage(1);
        const { data: questionBankResponse } = await fetchQuestionBank({
          variables: {
            assessmentId: resolvedAssessmentId,
            pagination: {
              socraticOffset: 0,
              socraticLimit: QUESTION_BANK_PAGE_SIZE,
              mcqOffset: 0,
              mcqLimit: QUESTION_BANK_PAGE_SIZE,
            },
          },
        });
        const responsePayload = questionBankResponse?.getAssessmentQuestionBank as
          | {
              socraticTotalCount?: number | null;
              mcqTotalCount?: number | null;
              data?: {
                aiTutors?: Array<{ questions?: Array<unknown> }>;
                mcqQuestions?: Array<unknown>;
              } | null;
            }
          | undefined;
        const fallbackSocraticTotal =
          responsePayload?.data?.aiTutors?.reduce((sum, tutor) => sum + (tutor.questions?.length ?? 0), 0) ?? 0;
        const fallbackMcqTotal = responsePayload?.data?.mcqQuestions?.length ?? 0;
        setSocraticQuestionBankTotal(responsePayload?.socraticTotalCount ?? fallbackSocraticTotal);
        setMcqQuestionBankTotal(responsePayload?.mcqTotalCount ?? fallbackMcqTotal);
        setQuestionGenerationFailed(false);
        setValidationError("");
        setIsQuestionBankOpen(true);
      } catch (generationError) {
        await rollbackAndClose(
          generationError instanceof Error ? generationError.message : "Failed to generate questions.",
          resolvedAssessmentId,
          didCreateInThisFlow
        );
      } finally {
        setIsGeneratingQuestionBank(false);
      }
    },
    [
      activeModuleData.id,
      createAssessmentMutation,
      fetchQuestionBank,
      generateAssessmentQuestionBankMutation,
      generatedAssessmentId,
      rollbackAndClose,
      setValidationErrorWithToast,
      updateAssessmentConfigMutation,
      waitForQuestionGenerationCompletion,
    ]
  );

  const onSubmit = async (data) => {
    const assessmentType: AssessmentType = data.assessmentType ?? DEFAULT_ASSESSMENT_TYPE;

    if (!data.selectedAiTutors || data.selectedAiTutors.length === 0) {
      setValidationErrorWithToast(NO_AI_TUTORS_ERROR);
      return;
    }

    const parsedNumberOfQuestions = parseIntegerInput(data.numberOfQuestions);
    if (isBlankInput(data.numberOfQuestions)) {
      setValidationErrorWithToast(NUMBER_OF_QUESTIONS_REQUIRED_ERROR);
      return;
    }

    if (
      parsedNumberOfQuestions === null ||
      parsedNumberOfQuestions < 1 ||
      parsedNumberOfQuestions > MAX_NUMBER_OF_QUESTIONS
    ) {
      setValidationErrorWithToast(NUMBER_OF_QUESTIONS_RANGE_ERROR);
      return;
    }

    const parsedTimeLimit = parseIntegerInput(data.timeLimit);
    const isTimeLimitBlank = isBlankInput(data.timeLimit);
    if (!isTimeLimitBlank && (parsedTimeLimit === null || parsedTimeLimit < 0 || parsedTimeLimit > MAX_TIME_LIMIT)) {
      setValidationErrorWithToast(TIME_LIMIT_RANGE_ERROR);
      return;
    }

    const normalizedTimeLimit = isTimeLimitBlank ? DEFAULT_TIME_LIMIT : parsedTimeLimit;
    if (normalizedTimeLimit === null) {
      setValidationErrorWithToast(TIME_LIMIT_RANGE_ERROR);
      return;
    }

    setValidationError("");
    const payload = {
      assessmentId: activeModuleData.assessment?.id,
      assessmentType,
      numberOfQuestions: parsedNumberOfQuestions,
      timeLimit: normalizedTimeLimit,
      aiTutorIds: data.selectedAiTutors,
      type: "Assessment",
      dueDate: data.dueDate ? data.dueDate : null,
      activityState: data.activityState,
      scheduledDate: data.scheduledDate,
    };

    if (!isNewActivity) {
      try {
        const poolCount = await getCurrentQuestionPoolCount(assessmentType);
        if (poolCount !== null && parsedNumberOfQuestions > poolCount) {
          setValidationErrorWithToast(buildInsufficientPoolMessage(parsedNumberOfQuestions, poolCount));
          return;
        }
      } catch (poolError) {
        setValidationErrorWithToast(
          poolError instanceof Error ? poolError.message : "Failed to validate question pool."
        );
        return;
      }

      onSave(payload);
      return;
    }

    await handleGenerateQuestions(payload);
  };

  const refreshQuestionBank = async ({
    socraticPage = socraticQuestionBankPage,
    mcqPage = mcqQuestionBankPage,
    assessmentIdOverride,
  }: {
    socraticPage?: number;
    mcqPage?: number;
    assessmentIdOverride?: string;
  } = {}) => {
    const effectiveAssessmentId = assessmentIdOverride ?? assessmentId;
    if (!effectiveAssessmentId) {
      return;
    }

    setSocraticQuestionBankPage(socraticPage);
    setMcqQuestionBankPage(mcqPage);
    const socraticOffset = (socraticPage - 1) * QUESTION_BANK_PAGE_SIZE;
    const mcqOffset = (mcqPage - 1) * QUESTION_BANK_PAGE_SIZE;

    const { data: questionBankResponse } = await fetchQuestionBank({
      variables: {
        assessmentId: effectiveAssessmentId,
        pagination: {
          socraticOffset,
          socraticLimit: QUESTION_BANK_PAGE_SIZE,
          mcqOffset,
          mcqLimit: QUESTION_BANK_PAGE_SIZE,
        },
      },
    });
    const responsePayload = questionBankResponse?.getAssessmentQuestionBank as
      | {
          socraticTotalCount?: number | null;
          mcqTotalCount?: number | null;
          data?: {
            aiTutors?: Array<{ questions?: Array<unknown> }>;
            mcqQuestions?: Array<unknown>;
          } | null;
        }
      | undefined;
    const fallbackSocraticTotal =
      responsePayload?.data?.aiTutors?.reduce((sum, tutor) => sum + (tutor.questions?.length ?? 0), 0) ?? 0;
    const fallbackMcqTotal = responsePayload?.data?.mcqQuestions?.length ?? 0;
    setSocraticQuestionBankTotal(responsePayload?.socraticTotalCount ?? fallbackSocraticTotal);
    setMcqQuestionBankTotal(responsePayload?.mcqTotalCount ?? fallbackMcqTotal);
  };

  const handleQuestionBankPageChange = async ({
    socraticPage,
    mcqPage,
  }: {
    socraticPage?: number;
    mcqPage?: number;
  }) => {
    await refreshQuestionBank({
      socraticPage: socraticPage ?? socraticQuestionBankPage,
      mcqPage: mcqPage ?? mcqQuestionBankPage,
    });
  };

  const handleOpenQuestionBank = async () => {
    if (!canViewQuestionBank || !assessmentId) return;

    setIsQuestionBankOpen(true);
    await refreshQuestionBank({ socraticPage: 1, mcqPage: 1 });
  };

  const handleCreateAssessment = useCallback(async () => {
    if (!isNewActivity) {
      setIsQuestionBankOpen(false);
      return;
    }

    if (!pendingCreatePayload?.assessmentId) {
      setValidationErrorWithToast("Generate questions first.");
      return;
    }

    try {
      await Promise.resolve(
        onSave({
          ...pendingCreatePayload,
          assessmentId: pendingCreatePayload.assessmentId,
          id: pendingCreatePayload.assessmentId,
        })
      );
    } catch (saveError) {
      setValidationErrorWithToast(saveError instanceof Error ? saveError.message : "Failed to create assessment.");
    }
  }, [isNewActivity, onSave, pendingCreatePayload, setValidationErrorWithToast]);

  useEffect(() => {
    if (!isNewActivity && activityData?.selectedAiTutorIds?.length > 0) {
      const selectedIds = activityData.selectedAiTutorIds;

      if (assessmentAiTutors?.getAssessmentAiTutors?.data?.pastModulesAiTutors?.length > 0) {
        const pastModulesWithSelections = assessmentAiTutors?.getAssessmentAiTutors?.data?.pastModulesAiTutors
          .map((module, index) => ({ module, index }))
          .filter(({ module }) => hasSelectedAiTutors(module.aiTutors, selectedIds))
          .map(({ index }) => index);

        if (pastModulesWithSelections.length > 0) {
          setShowPastModules(true);
          setOpenPastAccordionIndices(pastModulesWithSelections);
        }
      }

      if (assessmentAiTutors?.getAssessmentAiTutors?.data?.futureModulesAiTutors?.length > 0) {
        const futureModulesWithSelections = assessmentAiTutors?.getAssessmentAiTutors?.data?.futureModulesAiTutors
          .map((module, index) => ({ module, index }))
          .filter(({ module }) => hasSelectedAiTutors(module.aiTutors, selectedIds))
          .map(({ index }) => index);

        if (futureModulesWithSelections.length > 0) {
          setShowFutureModules(true);
          setOpenFutureAccordionIndices(futureModulesWithSelections);
        }
      }
    }
  }, [isNewActivity, activityData?.selectedAiTutorIds, assessmentAiTutors?.getAssessmentAiTutors?.data]);

  return (
    <FormLayout
      title={isNewActivity ? "Assessment" : "Edit Assessment"}
      isSubmitting={isSubmitting || isGeneratingQuestionBank}
      buttonsContainerStyle={{ justifyContent: "space-between" }}
      submitButtonStyle={styles.submitButton}
      cancelButtonStyle={styles.cancelButton}
      onCancel={onCancel}
      onSubmit={handleSubmit(onSubmit)}
      actionText={isNewActivity ? "Generate Questions" : "Save Changes"}
      disableSubmit={false}
      hasButtonLoader={true}
      error={error}
      contentContainerStyle={{ paddingBottom: 0 }}>
      <View style={styles.questionBankTopRight}>
        <CustomButton
          text={questionBankButtonText}
          useTranslationText={false}
          size="s"
          styleType="primaryLight"
          onPress={handleOpenQuestionBank}
          disabled={!canViewQuestionBank}
        />
      </View>
      {!canViewQuestionBank && !isGeneratingQuestionBank && (
        <>
          <CustomSpacing type="vertical" size="xs" />
          <View style={styles.questionBankTopRight}>
            <CustomText
              text={questionBankStatusMessage}
              useTranslationText={false}
              style={styles.subText}
              weight="normal"
            />
          </View>
        </>
      )}
      <CustomText text="Assessment Type" size="m" weight="ultraBold" useTranslationText={false} />
      <CustomText
        style={styles.subText}
        text="Select how students will complete this assessment."
        useTranslationText={false}
      />
      <CustomSpacing type="vertical" size="m" />

      <Controller
        name="assessmentType"
        control={control}
        render={({ field: { onChange, value } }) => (
          <>
            <View style={styles.assessmentTypeRow}>
              <RadioButton
                checked={(value ?? DEFAULT_ASSESSMENT_TYPE) === AssessmentType.SocraticDialog}
                disabled={isAssessmentTypeLocked}
                onPress={() => {
                  onChange(AssessmentType.SocraticDialog);
                  setValidationError("");
                }}
                label="Socratic Dialog"
              />
            </View>
            <CustomSpacing type="vertical" size="s" />
            <View style={styles.assessmentTypeRow}>
              <RadioButton
                checked={(value ?? DEFAULT_ASSESSMENT_TYPE) === AssessmentType.Mcq}
                disabled={isAssessmentTypeLocked}
                onPress={() => {
                  onChange(AssessmentType.Mcq);
                  setValidationError("");
                }}
                label="Multiple Choice (MCQ)"
              />
            </View>
          </>
        )}
      />
      {isAssessmentTypeLocked && (
        <>
          <CustomSpacing type="vertical" size="s" />
          <CustomText
            text={ASSESSMENT_TYPE_LOCKED_MESSAGE}
            size="s"
            useTranslationText={false}
            style={styles.subText}
          />
        </>
      )}

      <CustomSpacing type="vertical" size="xl" />

      <CustomText text="Number of Questions" size="m" weight="ultraBold" />
      <CustomText
        style={styles.subText}
        text="number_of_questions_subtitle"
        interpolation={{ maxQuestions: MAX_NUMBER_OF_QUESTIONS }}
      />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        name="numberOfQuestions"
        control={control}
        render={({ field: { onChange, onBlur, value } }) => (
          <CustomTextInput
            placeholder="e.g. 10"
            onBlur={onBlur}
            onChangeText={(text) => {
              onChange(text);
            }}
            value={toInputStringValue(value)}
            style={styles.textInput}
            keyboardType="numeric"
          />
        )}
      />

      <CustomSpacing type="vertical" size="xl" />
      <CustomText text="Time Limit (Optional)" size="m" weight="ultraBold" />
      <CustomText style={styles.subText} text="time_limit_subtitle" interpolation={{ maxTimeLimit: MAX_TIME_LIMIT }} />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        name="timeLimit"
        control={control}
        render={({ field: { onChange, onBlur, value } }) => (
          <CustomTextInput
            placeholder="e.g. 30"
            onBlur={onBlur}
            onChangeText={(text) => {
              onChange(text);
            }}
            value={value === undefined || value === null || value < 1 ? "" : String(value)}
            style={styles.textInput}
            keyboardType="numeric"
          />
        )}
      />

      <CustomSpacing type="vertical" size="xl" />
      <CustomText text="Due Date (Optional)" size="m" weight="ultraBold" />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        name="dueDate"
        control={control}
        rules={{ required: false }}
        render={({ field: { onChange, value } }) => (
          <DatePickerInput
            locale="en"
            value={value instanceof Date ? value : value ? new Date(value) : undefined}
            onChange={(date) => onChange(date ?? null)}
            onChangeText={(text) => {
              if (!text || text.trim() === "") {
                onChange(null);
              }
            }}
            inputMode="start"
            mode="outlined"
            outlineColor={"#D1D5DB"}
            selectionColor={undefined}
            activeOutlineColor={"blue"}
            withDateFormatInLabel={false}
            placeholder="MM/DD/YYYY"
            theme={{
              colors: {
                primary: "#000000",
                onSurface: "#000000",
              },
              roundness: 8,
            }}
            style={styles.datePicker}
            aria-label="Due Date date picker"
          />
        )}
      />

      <CustomSpacing type="vertical" size="xl" />
      <CustomText text="Use activities from this module" weight="ultraBold" />
      <CustomText style={styles.subText} text="Select content to generate questions from." />
      <CustomSpacing type="vertical" />
      <CustomText text="Current Module" weight="ultraBold" />
      <CustomSpacing type="vertical" size="xs" />

      <AssessmentFormAccordion
        title={assessmentAiTutors?.getAssessmentAiTutors?.data?.currentModuleAiTutors.title}
        aiTutors={assessmentAiTutors?.getAssessmentAiTutors?.data?.currentModuleAiTutors.aiTutors as AiTutor[]}
        control={control}
      />

      {assessmentAiTutors?.getAssessmentAiTutors?.data?.pastModulesAiTutors?.length > 0 && (
        <>
          <TouchableOpacity
            style={styles.includeModulesButton}
            onPress={() => {
              if (showPastModules) {
                setShowPastModules(false);
                setOpenPastAccordionIndices([]);
              } else {
                setShowPastModules(true);
                setOpenPastAccordionIndices(
                  assessmentAiTutors?.getAssessmentAiTutors?.data?.pastModulesAiTutors &&
                    assessmentAiTutors?.getAssessmentAiTutors?.data?.pastModulesAiTutors.length > 0
                    ? [0]
                    : []
                );
              }
            }}>
            <CustomText
              text={showPastModules ? "hide_other_modules" : "show_other_modules"}
              style={styles.includeModulesButtonText}
            />
          </TouchableOpacity>
          {showPastModules && (
            <>
              <CustomSpacing type="vertical" size="xxl" />
              <CustomText text="Past Modules" size="m" weight="ultraBold" />
              <CustomSpacing type="vertical" size="xs" />
              {assessmentAiTutors?.getAssessmentAiTutors?.data?.pastModulesAiTutors.map((module, index) => (
                <AssessmentFormAccordion
                  key={module.id}
                  title={module.title}
                  aiTutors={module.aiTutors as AiTutor[]}
                  control={control}
                  isOpen={openPastAccordionIndices.includes(index)}
                  onToggle={() => {
                    setOpenPastAccordionIndices((prev) =>
                      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
                    );
                  }}
                />
              ))}
            </>
          )}
        </>
      )}

      {assessmentAiTutors?.getAssessmentAiTutors?.data?.futureModulesAiTutors?.length > 0 && (
        <>
          <TouchableOpacity
            style={styles.includeModulesButton}
            onPress={() => {
              if (showFutureModules) {
                setShowFutureModules(false);
                setOpenFutureAccordionIndices([]);
              } else {
                setShowFutureModules(true);
                setOpenFutureAccordionIndices(
                  assessmentAiTutors?.getAssessmentAiTutors?.data?.futureModulesAiTutors &&
                    assessmentAiTutors?.getAssessmentAiTutors?.data?.futureModulesAiTutors.length > 0
                    ? [0]
                    : []
                );
              }
            }}>
            <CustomText
              text={showFutureModules ? "hide_future_modules" : "show_future_modules"}
              style={styles.includeModulesButtonText}
            />
          </TouchableOpacity>
          {showFutureModules && (
            <>
              <CustomSpacing type="vertical" size="xxl" />
              <CustomText text="Future Modules" size="m" weight="ultraBold" />
              <CustomSpacing type="vertical" size="xs" />
              {assessmentAiTutors?.getAssessmentAiTutors?.data?.futureModulesAiTutors.map((module, index) => (
                <AssessmentFormAccordion
                  key={module.id}
                  title={module.title}
                  aiTutors={module.aiTutors as AiTutor[]}
                  control={control}
                  isOpen={openFutureAccordionIndices.includes(index)}
                  onToggle={() => {
                    setOpenFutureAccordionIndices((prev) =>
                      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
                    );
                  }}
                />
              ))}
            </>
          )}
        </>
      )}

      <CustomSpacing type="vertical" size="m" />
      <View style={styles.activityState}>
        <CustomText text="Publish this Activity" size="l" weight="ultraBold" useTranslationText={false} />
        <CustomText
          text="Publish this activity now or schedule a publish date."
          size="s"
          useTranslationText={false}
          style={styles.stepDescription}
        />
        <Controller
          control={control}
          name="activityState"
          render={({ field: { onChange, value } }) => (
            <div style={{ display: "flex", gap: 10, marginTop: 5 }}>
              {Object.keys(ActivityState).map((state) => (
                <CustomButton
                  key={state}
                  text={ActivityState[state]}
                  size="s"
                  style={[styles.activityStateOption, ActivityState[state] === value && styles.activeState]}
                  onPress={() => {
                    onChange(ActivityState[state]);
                  }}
                />
              ))}
            </div>
          )}
        />
        {watchActivityState === ActivityState.Scheduled && (
          <>
            <CustomText
              text="This Activity will automatically be published after:"
              size="s"
              useTranslationText={false}
              style={styles.stepDescription}
            />
            <Controller
              control={control}
              name="scheduledDate"
              render={({ field: { onChange, value } }) => (
                <div style={{ maxWidth: 250, marginTop: 10 }}>
                  <DatePickerInput
                    locale="en"
                    value={value instanceof Date ? value : value ? new Date(value) : undefined}
                    onChange={onChange}
                    inputMode="start"
                    mode="outlined"
                    outlineColor={"#D1D5DB"}
                    selectionColor={undefined}
                    activeOutlineColor={"blue"}
                    withDateFormatInLabel={false}
                    placeholder="MM/DD/YYYY"
                    theme={{
                      colors: {
                        primary: "#000000",
                        onSurface: "#667085",
                      },
                      roundness: 8,
                    }}
                    style={styles.datePicker}
                    aria-label="Schedule published date picker"
                  />
                </div>
              )}
            />
          </>
        )}
      </View>

      {validationError && (
        <>
          <CustomSpacing type="vertical" size="s" />
          <CustomText text={validationError} size="s" style={styles.errorText} useTranslationText={false} />
        </>
      )}
      {isGeneratingQuestionBank && (
        <>
          <CustomSpacing type="vertical" size="s" />
          <CustomText
            text="Loading question bank. Please wait for question generation to finish. It takes about a minute to complete."
            useTranslationText={false}
            style={styles.subText}
          />
        </>
      )}
      <AssessmentQuestionBankModal
        visible={isQuestionBankOpen}
        onClose={() => setIsQuestionBankOpen(false)}
        isLoading={questionBankLoading}
        errorMessage={questionBankError?.message ?? null}
        assessmentId={assessmentId}
        assessmentType={questionBankDataNode?.assessmentType ?? watchAssessmentType}
        aiTutors={questionBankAiTutors as AiTutor[]}
        mcqQuestions={questionBankMcqQuestions as McqQuestion[]}
        submitText={isNewActivity ? "Create Assessment" : "Back"}
        onPrimaryAction={isNewActivity ? handleCreateAssessment : undefined}
        pageSize={QUESTION_BANK_PAGE_SIZE}
        socraticPage={socraticQuestionBankPage}
        mcqPage={mcqQuestionBankPage}
        socraticTotalCount={socraticQuestionBankTotal}
        mcqTotalCount={mcqQuestionBankTotal}
        numberOfQuestions={normalizedConfiguredQuestionCount}
        onPageChange={handleQuestionBankPageChange}
        onPoolConstraintViolation={(message) => {
          setValidationErrorWithToast(message);
        }}
        onRefresh={refreshQuestionBank}
      />
    </FormLayout>
  );
};

const styles = StyleSheet.create({
  textInput: {
    width: "100%",
  },
  subText: {
    color: "#667085",
    fontSize: 14,
    fontWeight: "400",
  },
  submitButton: {
    padding: 0,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
  },
  cancelButton: {
    backgroundColor: "transparent",
    borderWidth: 1,
    borderColor: "rgba(5, 6, 15, 0.10)",
    padding: 0,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
  },
  errorText: {
    color: "#E53E3E",
    fontSize: 14,
    fontWeight: "400",
  },
  includeModulesButton: {
    alignItems: "flex-start",
    marginTop: 12,
  },
  includeModulesButtonText: {
    color: "#3E68FE",
    fontSize: 14,
    fontWeight: "500",
    backgroundColor: "transparent",
    textDecorationLine: "underline",
  },
  questionBankTopRight: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
  },
  datePicker: {
    backgroundColor: "#FFFFFF",
    borderColor: "#D5D7DA",
    borderRadius: 10,
    color: "#000000",
    fontSize: 14,
  },
  activityState: {
    marginBottom: 20,
    padding: 15,
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
  },
  activeState: {
    backgroundColor: "#0084FF1A",
    color: "white",
    borderColor: "#0084FF",
  },
  activityStateOption: {
    borderRadius: 30,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderWidth: 2,
    borderStyle: "solid",
    color: "#667085",
    borderColor: "transparent",
  },
  stepDescription: {
    color: "#667085",
    marginTop: 5,
  },
  assessmentTypeRow: {
    paddingVertical: 6,
  },
});

export default AssessmentForm;
